import { z } from 'zod'
import dayjs from 'dayjs'
import { GPC_RATE_UNIT_PRICE_LIMIT } from '@/services/global/gpcRate/types'

// 단가 수정 모달 — 변경 단가(0 이상 100,000 미만 정수) · 적용 시작일(오늘 포함 미래). 생성본 폼 버스 값을 정규화한 뒤 검증한다.
//  문구는 PRD 화면 validation 원문 그대로 — priceInteger = No.1, startDatePast = No.4, priceRequired·startDateRequired = No.9.
export const GPC_RATE_EDIT_MESSAGES = {
  priceRequired: '변경 단가를 입력해 주세요.',
  priceInteger: '단가는 0 이상의 정수만 입력할 수 있습니다.',
  priceLimit: '단가는 100,000원 미만으로만 등록하실 수 있습니다.',
  startDateRequired: '적용 시작일을 선택해 주세요.',
  startDatePast: '적용 시작일은 오늘 이후 날짜만 지정할 수 있습니다. 이미 지난 입고분에는 단가가 소급 적용되지 않습니다.',
} as const

const DATE_FORMAT = 'YYYY-MM-DD'

export const gpcRateEditSchema = z.object({
  price: z
    .number({ required_error: GPC_RATE_EDIT_MESSAGES.priceRequired, invalid_type_error: GPC_RATE_EDIT_MESSAGES.priceRequired })
    .int(GPC_RATE_EDIT_MESSAGES.priceInteger)
    .min(0, GPC_RATE_EDIT_MESSAGES.priceInteger)
    .lt(GPC_RATE_UNIT_PRICE_LIMIT, GPC_RATE_EDIT_MESSAGES.priceLimit),
  startDate: z
    .string({ required_error: GPC_RATE_EDIT_MESSAGES.startDateRequired })
    .min(1, GPC_RATE_EDIT_MESSAGES.startDateRequired)
    // 레포에 dayjs customParseFormat 플러그인이 없어 형식은 정규식으로, 실존 날짜는 isValid 로 본다
    .refine((v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && dayjs(v).isValid(), GPC_RATE_EDIT_MESSAGES.startDateRequired)
    .refine((v) => !dayjs(v).isBefore(dayjs(), 'day'), GPC_RATE_EDIT_MESSAGES.startDatePast),
})

export type GpcRateEditSchemaType = z.infer<typeof gpcRateEditSchema>

/**
 * 폼 버스 값 → 스키마 입력. InputNumber 는 number|null|string(포매터), DatePicker 는 dayjs|null|string 을 올린다.
 * 값이 없으면 undefined 로 두어 required_error 가 뜨게 한다.
 */
export const toEditForm = (form: Record<string, unknown>): { price?: number; startDate?: string } => {
  const rawPrice = form.price
  const price =
    typeof rawPrice === 'number'
      ? rawPrice
      : typeof rawPrice === 'string' && rawPrice.replace(/,/g, '').trim() !== ''
        ? Number(rawPrice.replace(/,/g, ''))
        : undefined
  const rawDate = form.startDate
  const startDate = dayjs.isDayjs(rawDate)
    ? rawDate.format(DATE_FORMAT)
    : typeof rawDate === 'string' && rawDate.trim()
      ? dayjs(rawDate).format(DATE_FORMAT)
      : undefined
  return { ...(price !== undefined ? { price } : {}), ...(startDate !== undefined ? { startDate } : {}) }
}
